import { useState } from "react";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { Modal } from "../../../components/core/basic/Modal";
import { Button } from "../../../components/core/basic/Button";
import { CurrencyInput } from "../../../components/fields/base/NumberInput";
import { useBulkUpdateAdPropertiesV2 } from "../api/useBulkUpdateAdPropertiesV2";
import type { BulkAdPropertyUpdate } from "../../../zodios/apiDefinitions/api/ads";

interface BulkMinMaxBidsModalProps {
  advertisementIds: number[];
}

export const BulkMinMaxBidsModal = NiceModal.create(({ advertisementIds }: BulkMinMaxBidsModalProps) => {
  const { t } = useTranslation(["advertising", "common"]);
  const modal = useModal();
  const bulkUpdatePropertiesMutation = useBulkUpdateAdPropertiesV2();
  const [minBid, setMinBid] = useState<number | null>(null);
  const [maxBid, setMaxBid] = useState<number | null>(null);

  const hasValue = minBid !== null || maxBid !== null;

  const handleConfirm = () => {
    const updates: BulkAdPropertyUpdate["updates"] = [];

    if (minBid !== null) {
      updates.push({ field: "min_bid", action: "set", value: minBid });
    }
    if (maxBid !== null) {
      updates.push({ field: "max_bid", action: "set", value: maxBid });
    }

    bulkUpdatePropertiesMutation.mutate(
      {
        advertisements: advertisementIds,
        updates,
      },
      {
        onSuccess: () => modal.remove(),
      },
    );
  };

  return (
    <Modal
      isOpen={modal.visible}
      isDismissable
      onOpenChange={(isOpen) => !isOpen && modal.remove()}
      title={t(($) => $.advertising["Update Min and Max Bids"])}
      css={{ minWidth: "40vw" }}
    >
      <VStack gap="large" alignItems="stretch" paddingY="xlarge" paddingX="xlarge">
        <p className={css({ textAlign: "center" })}>
          {t(($) => $.advertising["Update the min and/or max bid for the selected media plans."], {
            count: advertisementIds.length,
          })}
        </p>
        <HStack justifyContent="center" gap="large">
          <CurrencyInput
            label={t(($) => $.advertising["Min Bid"])}
            value={minBid ?? undefined}
            onChange={(value) => setMinBid(Number.isNaN(value) ? null : value)}
            minValue={0}
            css={{ maxWidth: "200px" }}
          />
          <CurrencyInput
            label={t(($) => $.advertising["Max Bid"])}
            value={maxBid ?? undefined}
            onChange={(value) => setMaxBid(Number.isNaN(value) ? null : value)}
            minValue={0}
            css={{ maxWidth: "200px" }}
          />
        </HStack>
        <HStack justifyContent="space-around">
          <Button variant="secondary" onPress={() => modal.remove()}>
            {t(($) => $.common.actions["Cancel"])}
          </Button>
          <Button onPress={handleConfirm} isPending={bulkUpdatePropertiesMutation.isPending} isDisabled={!hasValue}>
            {t(($) => $.advertising["Confirm"])}
          </Button>
        </HStack>
      </VStack>
    </Modal>
  );
});
